import React, { useCallback, useMemo } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, subDays } from 'date-fns';
import clsx from 'clsx';
import { getDateString, offeringStartsFrom, offeringEndsAt } from '@camp67/model/booking/dates';
import { TENT_TYPE_V1_TO_NIVAAS_SLUG } from '@camp67/model';
import { useBrandTheme } from '../brand-theme';
import type { RoomsPerDay } from '../../../state/reservation-store';
import type { TentType } from '../types';

interface VisualCalendarProps {
  minDate: Date;
  maxDate: Date;
  availableDates: RoomsPerDay;
  startDate: Date | null;
  endDate: Date | null;
  onStartDateSelect: (date: Date) => void;
  onEndDateSelect: (date: Date) => void;
  onClearDates?: () => void;
  rooms: [TentType, number][];
  bathingDates?: Date[];
  messages?: {
    checkIn: string;
    checkOut: string;
    nightsSelected: string;
    selectDates: string;
    bathingDate: string;
    fullyBooked: string;
    clearDates?: string;
  };
}

/**
 * Inline visual calendar with 2-month view.
 * Shows date range selection with availability indicators.
 */
export const VisualCalendar: React.FC<VisualCalendarProps> = ({
  minDate,
  maxDate,
  availableDates,
  startDate,
  endDate,
  onStartDateSelect,
  onEndDateSelect,
  onClearDates,
  rooms,
  bathingDates = [],
  messages = {
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    nightsSelected: 'nights',
    selectDates: 'Select dates',
    bathingDate: 'Bathing date',
    fullyBooked: 'Fully booked',
    clearDates: 'Clear dates',
  },
}) => {
  const { theme, brand } = useBrandTheme();

  // Generate unique class name for this calendar instance's brand-specific styles
  const calendarId = useMemo(() => `rdp-brand-${brand}`, [brand]);

  // Available dates as set for faster lookup
  const availableDateStrings = useMemo(
    () => new Set(Object.keys(availableDates)),
    [availableDates],
  );

  // Check if a date is unavailable
  const isDateUnavailable = useCallback(
    (date: Date, fromDate?: Date): boolean => {
      // Outside booking range
      if (date < subDays(new Date(), 1) || date >= offeringEndsAt) {
        return false; // Will be disabled, not unavailable
      }

      const dateString = getDateString(date);

      // No availability data for this date
      if (!availableDateStrings.has(dateString)) {
        return true;
      }

      const availableRooms = availableDates[dateString];
      if (!availableRooms) {
        return true;
      }

      // Check if requested rooms are available
      for (const [room, quantity] of rooms) {
        const nivaasSlug = (TENT_TYPE_V1_TO_NIVAAS_SLUG as Record<string, string>)[room];
        if (!nivaasSlug) continue;

        const roomIds = availableRooms.filter((id) => id?.includes(nivaasSlug));
        if (roomIds.length < quantity) {
          return true;
        }
      }

      // For end date selection, check continuous availability
      if (fromDate && date > fromDate) {
        for (let d = new Date(fromDate); d < date; d.setDate(d.getDate() + 1)) {
          const dString = getDateString(d);
          if (!availableDateStrings.has(dString)) {
            return true;
          }
        }
      }

      return false;
    },
    [availableDates, availableDateStrings, rooms],
  );

  // Check if a date should be disabled
  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      // Before min date or after max date
      if (date < minDate || date > maxDate) {
        return true;
      }

      // Before today
      if (date < subDays(new Date(), 1)) {
        return true;
      }

      // After offering period
      if (date >= offeringEndsAt) {
        return true;
      }

      return isDateUnavailable(date, startDate || undefined);
    },
    [minDate, maxDate, startDate, isDateUnavailable],
  );

  // Handle date selection
  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range) return;

      // If we have a start date but no end date, and user clicks a new date
      if (startDate && !endDate && range.to) {
        // If the new date is before start, make it the new start
        if (range.to < startDate) {
          onStartDateSelect(range.to);
        } else {
          onEndDateSelect(range.to);
        }
      } else if (range.from) {
        // New selection - start fresh
        onStartDateSelect(range.from);
      }
    },
    [startDate, endDate, onStartDateSelect, onEndDateSelect],
  );

  // Calculate nights
  const nights = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  }, [startDate, endDate]);

  // Selected range for display
  const selected: DateRange | undefined = startDate
    ? { from: startDate, to: endDate || undefined }
    : undefined;

  // Whether we have any selection
  const hasSelection = startDate || endDate;

  return (
    <div className="w-full">
      {/* Date summary header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-6">
          {/* Check-in */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {messages.checkIn}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {startDate ? format(startDate, 'EEE, MMM d') : '--'}
            </p>
          </div>

          {/* Arrow */}
          <div className="text-gray-300">
            <ArrowRightIcon className="w-5 h-5" />
          </div>

          {/* Check-out */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {messages.checkOut}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {endDate ? format(endDate, 'EEE, MMM d') : '--'}
            </p>
          </div>
        </div>

        {/* Nights badge & Clear button */}
        <div className="flex items-center gap-2">
          {nights > 0 && (
            <div 
              className="px-3 py-1.5 rounded-full text-sm font-semibold"
              style={{ backgroundColor: theme.calendarBadgeBgHex, color: theme.calendarBadgeTextHex }}
            >
              {nights} {messages.nightsSelected}
            </div>
          )}
          {hasSelection && onClearDates && (
            <button
              type="button"
              onClick={onClearDates}
              className="px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            >
              {messages.clearDates || 'Clear'}
            </button>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
        {/* Inject brand-specific calendar styles */}
        <style>{`
          .${calendarId} .rdp-day_range_start,
          .${calendarId} .rdp-day_range_end,
          .${calendarId} .rdp-day_selected {
            background-color: ${theme.calendarSelectedHex} !important;
            color: white !important;
          }
          .${calendarId} .rdp-day_range_start:hover,
          .${calendarId} .rdp-day_range_end:hover,
          .${calendarId} .rdp-day_selected:hover {
            background-color: ${theme.calendarSelectedHex} !important;
          }
          .${calendarId} .rdp-day_range_middle {
            background-color: ${theme.calendarRangeBgHex} !important;
            color: ${theme.calendarRangeTextHex} !important;
          }
        `}</style>
        <DayPicker
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={2}
          fromDate={minDate}
          toDate={maxDate}
          disabled={isDateDisabled}
          showOutsideDays
          fixedWeeks
          className={clsx('rdp-v4', calendarId)}
          classNames={{
            months: 'flex flex-col md:flex-row gap-4 md:gap-8',
            month: 'flex-1',
            caption: 'flex justify-center pt-1 relative items-center mb-4',
            caption_label: 'text-base font-semibold text-gray-900',
            nav: 'flex items-center gap-1',
            nav_button:
              'h-8 w-8 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 disabled:opacity-50',
            nav_button_previous: 'absolute left-0',
            nav_button_next: 'absolute right-0',
            table: 'w-full border-collapse',
            head_row: 'flex',
            head_cell: 'text-gray-500 rounded-md w-10 font-medium text-xs uppercase',
            row: 'flex w-full mt-1',
            cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
            day: 'h-10 w-10 p-0 font-medium rounded-full hover:bg-gray-100 aria-selected:opacity-100',
            day_range_start: 'day-range-start',
            day_range_end: 'day-range-end',
            day_selected: '',
            day_today: 'bg-gray-100 text-gray-900',
            day_outside: 'text-gray-400 opacity-50',
            day_disabled: 'text-gray-300 cursor-not-allowed',
            day_range_middle: 'rounded-none',
            day_hidden: 'invisible',
          }}
          modifiers={{
            bathingDate: bathingDates,
            unavailable: (date) =>
              date >= offeringStartsFrom &&
              date < offeringEndsAt &&
              isDateUnavailable(date),
          }}
          modifiersClassNames={{
            bathingDate: 'ring-2 ring-orange-400 ring-inset',
            unavailable: 'bg-red-50 text-red-300 line-through',
          }}
        />

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200 text-xs">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: theme.calendarSelectedHex }}
            />
            <span className="text-gray-600">Selected</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-orange-100 ring-2 ring-orange-400 ring-inset" />
            <span className="text-gray-600">{messages.bathingDate}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
              <span className="text-red-300 text-[10px] line-through">15</span>
            </div>
            <span className="text-gray-600">{messages.fullyBooked}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Icon component
const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

export default VisualCalendar;
